// Calendar utility functions

// Color utility functions
export const LightenDarkenColor = (col, amt) => {
    col = parseInt(col, 16);
    let color = (((col & 0x0000FF) + amt) | ((((col >> 8) & 0x00FF) + amt) << 8) | (((col >> 16) + amt) << 16)).toString(16);
    return "#" + color;
};

// Color management for resources
export const createResourceColorManager = (resources, currentDate, myEvents, setCalendarColors) => {
    return () => {
        // Dynamically create resource arrays
        const crewResource = resources.find(resource => resource.name === "Crew");
        const freelanceResources = crewResource?.children?.find(child => child.name === "Freelancers")?.children?.map(child => child.id) || [];
        const equipmentResources = resources
            .find(resource => resource.name === "Equipment")?.children?.map(child => child.id) || [];
        const allResources = resources
            .flatMap(resource => resource.children || [])
            .map(child => child.id);

        // Initialize colors with predefined values
        const colors = [
            {
                background: '#FFF3F4',
                resource: Array.from(Array(101).keys()), // Background for Sundays
                recurring: {
                    repeat: 'weekly',
                    weekDays: 'SU',
                },
            },
            {
                background: '#f7f2f2',
                resource: Array.from(Array(101).keys()), // Background for Saturdays
                recurring: {
                    repeat: 'weekly',
                    weekDays: 'SA',
                },
            },
            {
                date: currentDate, // Green background for "today" across all rows
                background: 'rgb(227, 255, 227)',
                resource: allResources,
            },
        ];

        const addColor = (startDate, endDate, resource, background, isEditable) => {
            const color = { start: startDate, end: endDate, resource, background };
            colors.push(color);

            if (!isEditable) {
                const nonEditableColor = { ...color, background: '#808080' }; // Non-editable color
                colors.push(nonEditableColor);
            }
        };

        if (myEvents && Array.isArray(myEvents)) {
            myEvents.forEach((event) => {
                // Initialize dates based on allDay flag
                let startDate, endDate;
                if (event.allDay) {
                    startDate = event.start ? new Date(event.start) : null;
                    endDate = event.end ? new Date(event.end) : null;
                    if (startDate) startDate.setHours(0, 0, 1);
                    if (endDate) endDate.setHours(23, 59, 59);
                } else {
                    startDate = event.start ? new Date(event.start) : null;
                    endDate = event.end ? new Date(event.end) : null;
                }

                // Check if startDate and endDate are valid
                if (!startDate || isNaN(startDate.getTime()) || !endDate || isNaN(endDate.getTime())) {
                    console.warn('Invalid event dates:', event);
                    return; // Skip this event if dates are invalid
                }

                // Check for freelance resources
                if (event.resource.some((r) => freelanceResources.includes(r))) {
                    const freelanceColorBackground = `${event.color}${Math.floor(0.4 * 255).toString(16).padStart(2, '0')}`;
                    addColor(startDate, endDate, [1001], freelanceColorBackground, event.editable);
                }

                // Check for equipment resources
                if (event.resource.some((r) => equipmentResources.includes(r))) {
                    const equipmentColorBackground = `${event.color}${Math.floor(0.4 * 255).toString(16).padStart(2, '0')}`;
                    addColor(startDate, endDate, [101], equipmentColorBackground, event.editable);
                }
            });

            // Set the new colors in the state
            setCalendarColors(colors);
        }
    };
};

// Cursor position calculation functions
export const createCursorPositionCalculators = (mobileShow, scrollPos) => ({
    cursorPosCalcX: (presence) => {
        // The cursor position includes the 194px offset, so we need to subtract it for display
        return presence.cursor.x - scrollPos.x - 194;
    },

    cursorPosCalcY: (presence) => {
        // The cursor position includes the 98px offset, so we need to subtract it for display
        // Also account for the difference between the other user's vertical scroll and our current scroll
        const otherUserVerticalScroll = presence.verticalScroll || 0;
        const verticalScrollDifference = otherUserVerticalScroll - scrollPos.y;
        const result = presence.cursor.y - 98 + verticalScrollDifference;
        
        
        return result;
    }
});

// Zoom level management
export const createZoomLevelManager = (zoomLevel, setZoomLevel, zoomLevels) => {
    return (text, inst) => {
        let currentZoom = zoomLevel;
        if (text === "minus") {
            if (currentZoom === 1440) {
                console.log('Zoom at maximum')
            } else {
                const idx = zoomLevels.findIndex((element) => element === currentZoom);
                if (idx !== -1 && idx + 1 < zoomLevels.length) {
                    setZoomLevel(zoomLevels[idx + 1]);
                    // Removed setSelectedDate call to prevent unnecessary rerenders
                }
            }
        } else {
            if (currentZoom === 360) {
                console.log('Zoom at minimum')
            } else {
                const idx = zoomLevels.findIndex((element) => element === currentZoom);
                if (idx > 0) {
                    setZoomLevel(zoomLevels[idx - 1]);
                    // Removed setSelectedDate call to prevent unnecessary rerenders
                }
            }
        }
    };
};

// Scroll to user function
export const createScrollToUser = (others, user) => {
    return (connectionId) => {
        console.log('🔍 scrollToUser called with connectionId:', connectionId);
        console.log('🔍 Available users:', others.map(({ connectionId, presence }) => ({
            connectionId,
            name: presence?.name,
            hasCursor: !!presence?.cursor,
            cursorX: presence?.cursor?.x
        })));
        
        // Skip if trying to scroll to self
        if (connectionId === 'self') {
            console.log('❌ Cannot scroll to self');
            return;
        }
        
        // Find the user by connectionId
        const targetUser = others.find(({ connectionId: id }) => id === connectionId);
        
        if (!targetUser) {
            console.log('❌ User not found with connectionId:', connectionId);
            return;
        }
        
        if (!targetUser.presence?.cursor) {
            console.log('❌ User has no cursor position:', connectionId);
            return;
        }
        
        // Try multiple selectors for the scroll container
        let container = document.querySelector('.mbsc-timeline-grid-scroll');
        if (!container) {
            container = document.querySelector('.mbsc-timeline-grid');
        }
        if (!container) {
            container = document.querySelector('[class*="timeline"]');
        }
        if (!container) {
            container = document.querySelector('.calendar-container');
        }
        
        if (!container) {
            console.log('❌ Scroll container not found. Available containers:', 
                Array.from(document.querySelectorAll('[class*="timeline"], [class*="scroll"], [class*="calendar"]'))
                    .map(el => el.className)
            );
            return;
        }
        
        const scrollPosition = targetUser.presence.cursor.x - 500;
        console.log('✅ Scrolling to position:', scrollPosition);
        
        // Smooth scroll to the position
        container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    };
};

// Helper function to find nested objects
export const findNestedObj = (entireObj, keyToFind, valToFind) => {
    let foundObj;
    JSON.stringify(entireObj, (_, nestedValue) => {
        if (nestedValue && nestedValue[keyToFind] === valToFind) {
            foundObj = nestedValue;
        }
        return nestedValue;
    });
    return foundObj;
};

